import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { AnalysisState } from './analysis-state';
import { CrossAnalysisRepositoryProvider, ArcGisQueryRepositoryProvider } from '../../../../shared/providers/repository-providers';
import { UbigeoProvider } from '../../shared/layer/ubigeo/ubigeo-provider';
import {
  CrossAnalysisEntity,
  IAnalysisUseCase,
  LayerEntity,
  OverlappingRightsUseCase,
  LandUseConflictUseCase,
  BufferZonePressureUseCase,
  RuralFormalizationUseCase,
  TerritorialConnectivityUseCase,
  WaterResourcePressureUseCase,
  EcosystemFragmentationUseCase,
} from '@gishub/domain';

/**
 * Registro: analysisId → caso de uso (motor de intersección real).
 * Los análisis sin motor aún se listan (deshabilitados, "próximamente").
 */
const ANALYSIS_ENGINES: Record<string, new (queryRepo: any) => IAnalysisUseCase> = {
  'overlapping-rights': OverlappingRightsUseCase,
  'land-use-conflict': LandUseConflictUseCase,
  'buffer-zone-pressure': BufferZonePressureUseCase,
  'rural-formalization': RuralFormalizationUseCase,
  'territorial-connectivity': TerritorialConnectivityUseCase,
  'water-resource-pressure': WaterResourcePressureUseCase,
  'ecosystem-fragmentation': EcosystemFragmentationUseCase,
};

/** Debounce del auto-refresh: colapsa cambio-de-ámbito + llegada de geometría. */
const AUTO_RUN_DELAY_MS = 400;

@Injectable()
export class AnalysisNotifier extends BaseNotifier<AnalysisState> {
  /** Catálogo cargado del repositorio. */
  private catalog: CrossAnalysisEntity[] = [];

  /** Token anti-carrera de ejecuciones (respuestas tardías se descartan). */
  private _runToken = 0;

  /** Timer del auto-refresh debounceado (cancelable en dispose()). */
  private _autoTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(ref: Ref) {
    super(ref, {
      ready: true,
      analyses: [],
      selectedId: null,
      running: false,
      error: null,
      result: null,
      layerErrors: [],
      scopeCode: null,
      scopeLabel: null,
      scopeGeometry: null,
      scopeDirty: false,
    });

    this.trackDisposable(() => {
      if (this._autoTimer != null) clearTimeout(this._autoTimer);
      this._autoTimer = null;
    });
  }

  private get ubigeo(): UbigeoProvider {
    return this.ref.watch(UbigeoProvider);
  }

  private get queryRepo() {
    return this.ref.read(ArcGisQueryRepositoryProvider).instance;
  }

  override build(): void {
    void this.loadCatalog();

    // ── Reactividad al ubigeo ────────────────────────────────────────────
    // Reacciona al código más específico (distrito > provincia > departamento)
    // y a la llegada de la geometría (async, después del cambio de código).
    // Marca el ámbito como desactualizado y relanza el análisis activo.
    this.listen(() => {
      const ub = this.ubigeo.state();
      const s = this.snapshot();

      const code =
        ub.selectedDistrict || ub.selectedProvince || ub.selectedDepartment || null;
      const geometry = ub.currentGeometry;

      const codeChanged = code !== s.scopeCode;
      const geometryChanged = geometry !== s.scopeGeometry;
      if (!codeChanged && !geometryChanged) return;

      const hadResult = s.result != null;
      const wasRunning = s.running;
      const label =
        ub.selectedDistrictName ||
        ub.selectedProvinceName ||
        ub.selectedDepartmentName ||
        '';

      this.updateState(prev => ({
        ...prev,
        ...(codeChanged ? { scopeCode: code, scopeLabel: label || null } : {}),
        scopeGeometry: geometry,
        scopeDirty: hadResult && !prev.running ? true : prev.scopeDirty,
      }));

      // Auto-ejecución: al cambiar de ámbito (si ya había resultado o había
      // una carrera en vuelo) o al llegar la geometría que define el área.
      const shouldRun = (codeChanged && (hadResult || wasRunning)) ||
        (geometryChanged && geometry != null && (hadResult || wasRunning));
      if (shouldRun) {
        this.scheduleAutoRun();
      }
    });
  }

  /** Motor del análisis seleccionado (o null si aún no tiene implementación). */
  private engineFor(id: string | null): IAnalysisUseCase | null {
    if (!id) return null;
    const Ctor = ANALYSIS_ENGINES[id];
    return Ctor ? new Ctor(this.queryRepo) : null;
  }

  /** true si el análisis tiene motor de intersección implementado. */
  hasEngine(id: string): boolean {
    return !!ANALYSIS_ENGINES[id];
  }

  private async loadCatalog(): Promise<void> {
    try {
      const repo = this.ref.read(CrossAnalysisRepositoryProvider).instance;
      const res = await repo.getAnalyses();
      if (res.success && res.data) {
        this.catalog = res.data;
        const firstWithEngine =
          res.data.find((a) => !!ANALYSIS_ENGINES[a.id])?.id ?? null;

        // Mantener la selección del usuario si el catálogo la conserva.
        const s = this.snapshot();
        const keep =
          s.selectedId && res.data.some((a) => a.id === s.selectedId)
            ? s.selectedId
            : firstWithEngine;

        this.updateState(prev => ({
          ...prev,
          analyses: res.data!,
          selectedId: keep,
        }));

        // Primer run automático: el panel nunca arranca vacío.
        if (this.snapshot().result == null) {
          this.scheduleAutoRun(0);
        }
      }
    } catch (err: any) {
      this.updateState((s) => ({
        ...s,
        error: err?.message ?? 'No se pudo cargar el catálogo de análisis',
      }));
    }
  }

  select(id: string): void {
    const s = this.snapshot();
    if (s.selectedId === id) return;
    // Invalida cualquier ejecución en vuelo del análisis anterior: sin esto,
    // la respuesta tardía se aplicaría al análisis recién seleccionado.
    this._runToken++;
    this.cancelAutoRun();
    this.updateState(prev => ({
      ...prev,
      selectedId: id,
      result: null,
      error: null,
      layerErrors: [],
    }));
    // Cambiar de análisis también ejecuta de inmediato (comportamiento reactivo).
    void this.runInternal(true);
  }

  /** Ejecución manual (botón "Ejecutar análisis"). Usa el ámbito activo. */
  async run(): Promise<void> {
    await this.runInternal(false);
  }

  /**
   * Auto-refresh debounceado: colapsa el cambio de ámbito y la llegada de la
   * geometría (eventos casi simultáneos) en una sola ejecución con el ámbito
   * final. delay=0 para el primer run tras cargar el catálogo.
   */
  private scheduleAutoRun(delay: number = AUTO_RUN_DELAY_MS): void {
    if (this._autoTimer != null) clearTimeout(this._autoTimer);
    this._autoTimer = setTimeout(() => {
      this._autoTimer = null;
      if (this._disposed) return;
      void this.runInternal(true);
    }, delay);
  }

  private cancelAutoRun(): void {
    if (this._autoTimer != null) {
      clearTimeout(this._autoTimer);
      this._autoTimer = null;
    }
  }

  /**
   * Núcleo de ejecución. Todas las entradas (manual, auto-refresh, cambio de
   * análisis) convergen aquí con token anti-carrera: solo la última ejecución
   * aplica su resultado al estado.
   */
  private async runInternal(auto: boolean): Promise<void> {
    const id = this.snapshot().selectedId;
    const engine = this.engineFor(id);
    const meta = this.catalog.find((a) => a.id === id) ?? null;

    if (!engine || !meta) {
      if (!auto) {
        this.updateState((s) => ({
          ...s,
          error: 'Este análisis estará disponible próximamente',
        }));
      }
      return;
    }

    const token = ++this._runToken;
    const requestedScopeCode = this.snapshot().scopeCode;
    const requestedScopeGeometry = this.snapshot().scopeGeometry;

    this.updateState(prev => ({
      ...prev,
      running: true,
      error: null,
      scopeDirty: false,
    }));

    try {
      // Capas del catálogo, clonadas para no mutar el catálogo.
      const layers = meta.layers.map((l) => new LayerEntity({ ...l }));

      const ub = this.ubigeo.state();
      const departmentName = this.normalizeDepartmentName(
        ub.selectedDepartmentName || null,
      );

      const res = await engine.execute(
        layers,
        requestedScopeCode,
        departmentName,
        requestedScopeGeometry,
      );

      // Respuesta tardía (cambió el análisis o el ámbito): descartar.
      if (token !== this._runToken || this._disposed) return;

      if (res.success && res.data) {
        this.updateState(prev => ({
          ...prev,
          running: false,
          result: res.data!,
          layerErrors: res.errors ?? [],
          error: null,
          // Si el ámbito cambió durante el vuelo, el resultado ya nace viejo.
          scopeDirty: this.snapshot().scopeCode !== requestedScopeCode,
        }));
      } else {
        this.updateState(prev => ({
          ...prev,
          running: false,
          error: res.message ?? 'El análisis no devolvió resultados',
          layerErrors: res.errors ?? [],
        }));
      }
    } catch (err: any) {
      if (token !== this._runToken || this._disposed) return;
      this.updateState((s) => ({
        ...s,
        running: false,
        error: err?.message ?? 'Error al ejecutar el análisis',
      }));
    }
  }

  /** 'Nacional' (etiqueta del selector) no es un departamento filtrable. */
  private normalizeDepartmentName(value: string | null): string | null {
    const v = (value ?? '').trim();
    return v.length > 0 && !/^nacional$/i.test(v) ? v : null;
  }
}
